import React from "react";
import ReactDOM from "react-dom/client";
import { DayPicker } from "react-day-picker";
import { fr } from "react-day-picker/locale";
import "react-day-picker/style.css";
import { format, set } from "date-fns";

/** @typedef {import('../services/BookingReservationService.js').ReactObj} ReactObj */
/** @typedef {import('../transformers/BookingReservationTransformer.js').ReservationRangeObj} ReservationRangeObj */
/** @typedef {import('../models/BookingResourceModel.js').BookingResourceItem} BookingResourceItem */

/**
 * @typedef {object} BookingFormStateObj
 * @property {Array<ReservationRangeObj>} disableDates - Liste des plages de dates déjà prises
 * @property {Array<Date|null>} currentRange - La plage de date actuellement sélectionnée
 * @property {number} bookingResourceId - L'identifiant de la ressource de réservation
 * @property {?BookingResourceItem} bookingResource - La ressource de réservation associée aux options du datepicker
 * @property {?string} startHourReservation - L'heure de début d'enregistrement de la ressource de réservation, au format "HH:mm"
 * @property {?string} endHourReservation - L'heure de fin d'enregistrement de la ressource de réservation, au format "HH:mm"
 * @property {?string} minDate - Date minimale sélectionnable
 * @property {?string} maxDate - Date maximale sélectionnable
 * @property {?number} minDurationMinutes - Durée minimale d'une réservation en minutes
 * @property {?number} maxDurationMinutes - Durée maximale d'une réservation en minutes
 * @property {string} dangerMessage - Message d'erreur à afficher si la plage sélectionnée est invalide
 * @property {?import('react-day-picker').DateRange} range - Plage de date sélectionnée pour le composant DayPicker
 */

/**
 * @typedef {object} BookingFormProps
 * @property {string} bookingResourceId - L'identifiant de la ressource de réservation
 */

class BookingForm extends React.Component {
  /**
   * @param {BookingFormProps} props
   */
  constructor(props) {
    super(props);
    this.state = /**@type {BookingFormStateObj}*/ ({
      disableDates: [],
      currentRange: [null, null],
      bookingResourceId: parseInt(props.bookingResourceId, 10),
      bookingResource: null,
      startHourReservation: null,
      endHourReservation: null,
      minDate: null,
      maxDate: null,
      minDurationMinutes: null,
      maxDurationMinutes: null,
      dangerMessage: "",
      range: null
    });
  }

  async componentDidMount() {
    const reactObj = await this.fetchReactObj();
    const bookingResource = reactObj.bookingResource;

    this.setState({
      disableDates: reactObj.disableDates.map(d => ({ from: new Date(d.from), to: new Date(d.to) })),
      bookingResource,
      startHourReservation: bookingResource.start_hour_registration,
      endHourReservation: bookingResource.end_hour_registration,
      minDate: reactObj.minDate,
      maxDate: reactObj.maxDate,
      minDurationMinutes: reactObj.minDurationMinutes,
      maxDurationMinutes: reactObj.maxDurationMinutes
    });
  }

  /**
   * Chargement des dates déjà prises via l'api
   * @returns {Promise<ReactObj>} La liste des dates déjà prises
   */
  async fetchReactObj() {
    var response = await fetch(`/api/booking-reservation/react-obj/search/${this.state.bookingResourceId}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  _getDisabledDates() {
    const disabledRanges = this.state.disableDates;
    const disabled = [];

    disabledRanges.forEach(range => {
      const from = new Date(range.from);
      const to = new Date(range.to);

      // Bloquer tous les jours entre from +1 et to -1
      let d = new Date(from);
      d.setDate(d.getDate() + 1); // commencer à partir du lendemain de from

      const end = new Date(to); // jour de départ reste libre pour check-in
      while (d < end) {
        disabled.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
    });

    // Bloquer toujours les jours passés
    disabled.push({ before: new Date() });

    return disabled;
  }

  /**
   * Permet de vérifier si une plage de date est disponible par rapport à une liste de plages déjà prises
   * @param {ReservationRangeObj} selectedRange - Plage de sélection
   * @param {Array<ReservationRangeObj>} disableRanges - Liste des plages indisponibles
   * @returns {boolean} true si la plage est disponible, false sinon
   * @private
   */
  _isRangeAvailable(selectedRange, disableRanges) {
    if (!selectedRange?.from || !selectedRange?.to) return true;

    const sFrom = set(selectedRange.from, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
    const sTo = set(selectedRange.to, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

    return !disableRanges.some(r => {
      const rFrom = set(r.from, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
      const rTo = set(r.to, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

      return sFrom < rTo && sTo > rFrom;
    });
  }

  /**
   * Permet de vérifier si une plage de date respecte la durée minimale requise
   * @param {ReservationRangeObj} selectedRange - Plage de sélection
   * @param {?number} minDurationMinutes - Durée minimale en minute
   * @returns {boolean} true si la plage respecte la durée minimale, false sinon
   * @private
   */
  _isRangeMinDuration(selectedRange, minDurationMinutes) {
    if (!minDurationMinutes) return true;

    return this._getRangeDurationMinutes(selectedRange) >= minDurationMinutes;
  }

  /**
   * Permet de vérifier si une plage de date respecte la durée maximale requise
   * @param {ReservationRangeObj} selectedRange - Plage de sélection
   * @param {?number} maxDurationMinutes - Durée maximale en minute
   * @return {boolean} true si la plage respecte la durée maximale, false sinon
   * @private
   */
  _isRangeMaxDuration(selectedRange, maxDurationMinutes) {
    if (!maxDurationMinutes) return true;

    return this._getRangeDurationMinutes(selectedRange) <= maxDurationMinutes;
  }

  /**
   * Permet de calculer la durée d'une plage de date en minutes
   * @param {ReservationRangeObj} selectedRange - Plage de sélection
   * @returns {number} La durée de la plage en minutes
   * @private
   */
  _getRangeDurationMinutes(selectedRange) {
    if (!selectedRange?.from || !selectedRange?.to) return 0;

    return (selectedRange.to.getTime() - selectedRange.from.getTime()) / 60000;
  }

  /**
   * Permet de vérifier si une plage de date a la même heure de début et de fin
   * @param {ReservationRangeObj} selectedRange - Plage de sélection
   * @returns {boolean} true si la plage a la même heure de début et de fin, false sinon
   * @private
   */
  _rangeHasSameTime(selectedRange) {
    if (!selectedRange?.from || !selectedRange?.to) return false;

    return selectedRange.from.getTime() === selectedRange.to.getTime();
  }

  /**
   * Gestion de la sélection d'une plage de date
   * @param {import('react-day-picker').DateRange} selectedRange - La plage de date sélectionnée
   */
  _onSelectDateRange(selectedRange) {
    if (selectedRange?.from && selectedRange?.to) {
      const fromHours = this.state.startHourReservation ? this.state.startHourReservation.split(":").map(Number)[0] : 0;
      const fromMinutes = this.state.startHourReservation ? this.state.startHourReservation.split(":").map(Number)[1] : 0;
      const toHours = this.state.endHourReservation ? this.state.endHourReservation.split(":").map(Number)[0] : 0;
      const toMinutes = this.state.endHourReservation ? this.state.endHourReservation.split(":").map(Number)[1] : 0;
      const modifiedSelectedRange = {
        from: set(selectedRange.from, { hours: fromHours, minutes: fromMinutes, seconds: 0, milliseconds: 0 }),
        to: set(selectedRange.to, { hours: toHours, minutes: toMinutes, seconds: 0, milliseconds: 0 })
      };
      const rangeToCheckDuration = {
        from: set(selectedRange.from, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
        to: set(selectedRange.to, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
      };

      this.setState({
        range: modifiedSelectedRange,
        dangerMessage: ""
      });

      if (this._rangeHasSameTime(rangeToCheckDuration)) {
        this.setState({ dangerMessage: "La date de début et la date de fin ne peuvent pas être identiques" });
        return;
      }

      if (this.state.disableDates.length && !this._isRangeAvailable(modifiedSelectedRange, this.state.disableDates)) {
        this.setState({ dangerMessage: "Cette plage chevauche une réservation existante ou n'est pas accessible pour une réservation" });
        return;
      }

      if (this.state.minDurationMinutes && !this._isRangeMinDuration(rangeToCheckDuration, this.state.minDurationMinutes)) {
        this.setState({ dangerMessage: `Vous devez choisir une période d'au moins ${this._getDurationText()}` });
        return;
      }

      if (this.state.maxDurationMinutes && !this._isRangeMaxDuration(rangeToCheckDuration, this.state.maxDurationMinutes)) {
        this.setState({ dangerMessage: `Vous avez choisie une période d'au plus ${this._getDurationText()}` });
        return;
      }
    }
  }

  /**
   * Permet de formater la durée maximale en texte lisible
   * @returns {string} Le texte représentant la durée
   * @private
   */
  _getDurationText() {
    const maxDuration = /** @type {number} */ (this.state.maxDurationMinutes);
    let durationText;

    if (maxDuration > 1440) {
      durationText = `${Math.floor(maxDuration / 1440)} jour(s)`;
    } else if (maxDuration > 60) {
      durationText = `${Math.floor(maxDuration / 60)} heure(s)`;
    } else {
      durationText = `${maxDuration} minute(s)`;
    }

    return durationText;
  }

  render() {
    return (
      <div style={{ display: "grid", justifyContent: "center", gridTemplateColumns: "auto", gap: "8px" }}>
        <DayPicker locale={fr} mode="range" selected={this.state.range} onSelect={this._onSelectDateRange.bind(this)} disabled={this._getDisabledDates()} style={{ margin: "0 auto" }} navLayout="around" captionLayout="dropdown" />
        <div style={{ textAlign: "center" }}>
          {this.state.dangerMessage ? (
            <>
              <div className="alert alert--danger">{this.state.dangerMessage}</div>
              <div className="space--8"></div>
            </>
          ) : (
            ""
          )}
          {this.state.range?.from && this.state.range?.to && !this.state.dangerMessage ? (
            <div style={{ display: "grid", justifyContent: "center", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div className="badge badge--info badge--center">Du {format(this.state.range.from, "dd/MM/yyyy HH:mm")}</div>
              <div className="badge badge--info badge--center">Au {format(this.state.range.to, "dd/MM/yyyy HH:mm")}</div>
              <input type="hidden" name="bookingReservation[start_date]" value={format(this.state.range.from, "dd/MM/yyyy")} />
              <input type="hidden" name="bookingReservation[end_date]" value={format(this.state.range.to, "dd/MM/yyyy")} />
            </div>
          ) : (
            "Aucune plage sélectionnée"
          )}
        </div>
      </div>
    );
  }
}

const bookingFormNode = document.getElementById("booking-form");

if (bookingFormNode) {
  const root = ReactDOM.createRoot(bookingFormNode);

  root.render(<BookingForm bookingResourceId={bookingFormNode.getAttribute("data-bookingresourceid")} />);
}
